import { useState, useEffect } from 'react';
import { AuditLog, UserRole } from '../../types';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';

type AuditLogWithActor = AuditLog & { profiles: { full_name: string; role: UserRole } | null };

export const AdminContactChanges = () => {
  const [logs, setLogs] = useState<AuditLogWithActor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('audit_logs')
        .select('*, profiles(full_name, role)')
        .eq('entity_type', 'phone_book')
        .order('created_at', { ascending: false })
        .limit(500);
      if (dbError) throw dbError;
      setLogs((data ?? []) as AuditLogWithActor[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load change logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadLogs} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact Change Logs</h1>
        <p className="text-sm text-gray-600">Track which employees added or edited contacts. Only visible to Admins.</p>
      </div>
      {logs.length === 0 ? (
        <Empty title="No change records" message="No contact add/edit logs found." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">At</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Old Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">New Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((log) => {
                const newData = (log.new_data as Record<string, unknown> | null) ?? {};
                const oldData = (log.old_data as Record<string, unknown> | null) ?? {};
                return (
                  <tr key={log.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.action}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {log.profiles?.full_name ?? '-'}{' '}
                      {log.profiles?.role && `(${log.profiles.role})`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{(newData.name ?? oldData.name ?? '-') as string}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{(newData.phone_number ?? oldData.phone_number ?? '-') as string}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <pre className="max-w-xs overflow-auto">{JSON.stringify(oldData, null, 2)}</pre>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <pre className="max-w-xs overflow-auto">{JSON.stringify(newData, null, 2)}</pre>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
